/**
 * Encodes a string into Base64 format.
 *
 * @param data The string to encode.
 * @returns The Base64 encoded string.
 */
export function base64(data: string): string {
  const bytes = new TextEncoder().encode(data); // Encode string to UTF-8 bytes

  // Convert Uint8Array to a binary string (each character is a byte value)
  // This is more robust than String.fromCodePoint(...bytes) or String.fromCharCode(...bytes)
  // for large inputs, which can cause "Maximum call stack size exceeded" errors.
  let binaryString = '';
  bytes.forEach((byte) => {
    binaryString += String.fromCharCode(byte);
  });

  return btoa(binaryString); // Use browser's built-in btoa
}

/**
 * Encodes a string into Base64URL format.
 * Base64URL is a URL-safe version of Base64.
 *
 * @param data The string to encode.
 * @returns The Base64URL encoded string.
 */
export function base64Url(data: string): string {
  const base64Data = base64(data); // Get standard Base64
  // Convert Base64 to Base64URL:
  // Replace '+' with '-', '/' with '_', and remove trailing '=' padding.
  return base64Data.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Decodes a Base64 encoded string.
 *
 * @param base64Data The Base64 encoded string.
 * @returns The decoded string.
 * @throws {DOMException} If the input string is not correctly encoded.
 */
export function decode64(base64Data: string): string {
  const binString = atob(base64Data); // Use browser's built-in atob

  // Convert binary string back to Uint8Array
  const bytes = Uint8Array.from(binString, (m) => m.codePointAt(0)!);

  return new TextDecoder().decode(bytes); // Decode UTF-8 bytes to string
}

/**
 * Decodes a Base64URL encoded string.
 *
 * @param base64UrlData The Base64URL encoded string.
 * @returns The decoded string.
 * @throws {DOMException} If the input string (after conversion from Base64URL) is not correctly encoded.
 */
export function decode64Url(base64UrlData: string): string {
  // Convert Base64URL back to Base64:
  // Replace '-' with '+', '_' with '/'.
  let base64Padded = base64UrlData.replace(/-/g, '+').replace(/_/g, '/');

  // Add back padding if it was removed.
  // The length of a Base64 string (with padding) must be a multiple of 4.
  while (base64Padded.length % 4) {
    base64Padded += '=';
  }

  return decode64(base64Padded);
}
